

import React, { useEffect, useRef, useState } from "react";
import { FiFilter, FiX, FiRefreshCcw } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaDownload, FaSearch, FaSortUp, FaSortDown } from "react-icons/fa";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { sendGlobalWS, useWebSocket } from "../../context/UseWebSocketContext";
import { toast } from "react-toastify";
import { updateLosssOrProfitService } from "../apiservice/updateLossOrProfitService";
import { useAuth } from "../../context/auth";
import { closeAllPositionService } from "../apiservice/closeAllPositionService";
import { exitRowPositionService } from "../apiservice/exitRowPositionService";
import { Modal, Button } from "react-bootstrap";

const PositionTable = ({ darkMode = false }) => {
  const { getLoginToken, getGoogleAuthToken, getSecKey } = useAuth();
  const token = getLoginToken() || getGoogleAuthToken();
  
  // ✅ Get positions directly from WebSocket
  const { positionsData } = useWebSocket();
  const positions = Array.isArray(positionsData) ? positionsData : [];

  const [sortOrder, setSortOrder] = useState("desc");
  const [searchText, setSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState("trading_symbol");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [exitPopup, setExitPopup] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [profitAmount, setProfitAmount] = useState(0);
  const [lossAmount, setLossAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const rowsPerPage = 15;
  const isMountedRef = useRef(true);

  // ✅ Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ Stop loading when data arrives
  useEffect(() => {
    if (isLoading && isMountedRef.current) {
      setIsLoading(false);
      console.log("✅ Positions loaded, count:", positions.length);
    }
  }, [positions, isLoading]);

  // ✅ Load positions on mount
  useEffect(() => {
    console.log("🔄 Loading positions...");
    sendGlobalWS({ r: "positions" });
    
    // Timeout to stop loading after 8 seconds
    const timeout = setTimeout(() => {
      if (isMountedRef.current && isLoading) {
        setIsLoading(false);
      }
    }, 8000);
    
    return () => clearTimeout(timeout);
  }, []);

  // Apply dark mode
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    setCurrentPage(1);
  }, [positions, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target) && !searchText) {
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchText]);

  const handleRefresh = () => {
    console.log("🔄 Manual refresh triggered");
    setIsLoading(true);
    sendGlobalWS({ r: "positions" });
    toast.info("Refreshing positions...");
    
    setTimeout(() => {
      if (isMountedRef.current && isLoading) {
        setIsLoading(false);
      }
    }, 8000);
  };

  const handleDownload = () => {
    if (!positions || positions.length === 0) {
      toast.warning("No data to download!");
      return;
    }

    const headers = ['Account', 'Symbol', 'Position', 'Total Buy', 'Total Sell', 'Avg Buy', 'Avg Sell', 'Realized PnL', 'Unrealized PnL', 'LTP'];
    const rows = positions.map(row => [
      row.account_id,
      row.trading_symbol,
      row.pos,
      row.total_bought,
      row.total_sold,
      row.avg_buy,
      row.avg_sell,
      row.realized,
      row.unrealized,
      row.ltp
    ]);

    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `positions-data-${timestamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Download started!");
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FiFilter size={9} className="opacity-50" />;
    return sortOrder === "asc" ? <FaSortUp size={9} /> : <FaSortDown size={9} />;
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const clearSearch = () => {
    setSearchText('');
    setIsSearchExpanded(false);
    setCurrentPage(1);
  };

  const updateProfitLoss = async () => {
    const data = {
      profit_amount: profitAmount,
      loss_amount: lossAmount,
    };

    try {
      const response = await updateLosssOrProfitService(data, token);
      if (response.status === 200) {
        if (response?.data?.error === false) {
          toast.success(response?.data?.data);
          setShowLimit(false);
        } else {
          toast.error(response?.data?.data);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.data?.message || "Update failed");
    }
  };

  const handleCloseAllPosition = async () => {
    const secKey = getSecKey();
    try {
      const response = await closeAllPositionService(secKey);
      if (response.status === 200) {
        if (response?.data?.error === false) {
          toast.success(response?.data?.data);
          setExitPopup(false);
          handleRefresh();
        } else {
          toast.error(response?.data?.data);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.data || "Failed to close positions");
    }
  };

  const handleExitRowPosition = async (id, position) => {
    const data = { ins_id: id };
    try {
      const response = await exitRowPositionService(token, data);
      if (response.status === 200) {
        if (response?.data?.error === false) {
          const details = response?.data?.data;
          const lotSize = details.lot_size || 1;
          const quantity = Math.floor(Math.abs(position) / lotSize);
          
          const eventData = {
            symbol: details.symbol,
            exchange: details.exchange,
            contractType: details.contract_type,
            monthlyExpiry: details.monthly_expiry,
            weeklyExpiry: details.weekly_expiry,
            exactStrike: details.strike,
            action: position > 0 ? "SELL" : "BUY",
            quantity: quantity || 1,
            quantityType: "normal",
            showExpiry1: details.monthly_expiry ? "month" : "week"
          };
          
          window.dispatchEvent(new CustomEvent("fill-order-form", { detail: eventData }));
          toast.success("Pre-filled order form to exit " + details.symbol);
        } else {
          toast.error(response?.data?.data || "Failed to fetch symbol details");
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.data || "Failed to exit position");
    }
  };

  // Filter and sort data
  let filteredData = [...positions].filter(row => {
    const searchLower = searchText.toLowerCase();
    if (!searchLower) return true;
    return (
      row.trading_symbol?.toLowerCase().includes(searchLower) ||
      row.account_id?.toLowerCase().includes(searchLower)
    );
  });

  filteredData = filteredData.filter((row, index, self) => 
    index === self.findIndex(r => r.trading_symbol === row.trading_symbol)
  );

  filteredData.sort((a, b) => {
    let aVal = a[sortColumn] || '';
    let bVal = b[sortColumn] || '';
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    
    if (sortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentRows = filteredData.slice(startIdx, startIdx + rowsPerPage);

  // CSS classes
  const containerClass = `transition-colors duration-200 h-full flex flex-col bg-[var(--bg-primary)]`;
  const cardClass = `rounded-lg overflow-hidden transition-colors duration-200 flex flex-col h-full
    bg-[var(--bg-card)] border border-[var(--border-color)] shadow-md`;
  const buttonClass = `p-1 rounded-md transition-all duration-200 text-[var(--text-muted)] hover:bg-[var(--bg-hover)]`;
  const tableHeaderClass = `sticky top-0 z-10 transition-colors duration-200 text-[11px] font-semibold bg-[var(--bg-header)] text-[var(--text-primary)]`;
  const tableCellClass = `px-2 py-1 border text-[11px] transition-colors duration-200 border-[var(--border-color)] text-300 `;
  const searchBoxClass = `relative flex items-center rounded-md transition-all duration-300
    bg-[var(--input-bg)] border border-[var(--input-border)]
    ${isSearchExpanded ? 'w-52' : 'w-auto'}`;
  const searchInputClass = `w-full outline-none text-[11px] py-1 px-1 bg-transparent text-[var(--input-text)] placeholder:text-[var(--text-muted)]`;
  const flatAllBtnClass = `px-2 py-0.5 rounded-md text-[10px] font-medium transition-all duration-200 bg-gray-200 hover:bg-gray-500 text-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700`;
  const setThresholdBtnClass = `px-2 py-0.5 rounded-md text-[10px] font-medium transition-all duration-200 bg-gray-200 hover:bg-gray-500 text-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700`;
  const exitBtnClass = `px-1.5 py-0.5 rounded text-[9px] font-medium transition-all duration-200 bg-red-500 hover:bg-red-600 text-gray-400 dark:text-gray-200`;

  return (
    <div className={containerClass}>
      <div className={cardClass}>
        {/* Header */}
        <div className="flex-shrink-0 px-2 py-1 border-b border-[var(--border-color)] bg-[var(--bg-card)]">
          <div className="flex items-center justify-between gap-2">
            {/* Left Side Buttons */}
            <div className="flex  items-center gap-1">
              <button onClick={() => setExitPopup(true)} className={flatAllBtnClass}>
                Flat All
              </button>

              <button onClick={() => setShowLimit(true)} className={setThresholdBtnClass}>
                Set Threshold
              </button>

              {/* Search */}
              <div className="flex items-center gap-1">
                <div ref={searchContainerRef} className={searchBoxClass}>
                  <button
                    onClick={handleSearchExpand}
                    className={`p-1 rounded-l-md transition-all duration-200
                      ${isSearchExpanded 
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                      }`}
                  >
                    <FaSearch size={10} />
                  </button>
                  
                  <div className={`overflow-hidden transition-all duration-300 ${isSearchExpanded ? 'w-36' : 'w-0'}`}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      className={searchInputClass}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  
                  {isSearchExpanded && searchText && (
                    <button
                      onClick={clearSearch}
                      className={`p-1 rounded-r-md transition-all duration-200 hover:bg-[var(--bg-hover)] text-[var(--text-muted)]`}
                    >
                      <FiX size={10} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              <button onClick={handleDownload} className={buttonClass} title="Download">
                <FaDownload size={11} />
              </button>
              <button onClick={handleRefresh} className={buttonClass} title="Refresh">
                <FiRefreshCcw size={11} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-[11px] text-blue-600 dark:text-blue-400 font-medium">
              Loading positions...
            </span>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto min-h-0">
          <table className="w-full h-full">
            <thead className={tableHeaderClass}>
              <tr>
                <th className="px-2 py-1 border-b text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                  <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('account_id')}>
                    Account
                    <span className="text-blue-500">{getSortIcon('account_id')}</span>
                  </div>
                </th>
                <th className="px-2 py-1 border-b text-left font-semibold w-24 sticky top-0 bg-inherit border-[var(--border-color)]">
                  <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('trading_symbol')}>
                    Symbol
                    <span className="text-blue-500">{getSortIcon('trading_symbol')}</span>
                  </div>
                </th>
                <th className="px-2 py-1 border-b text-left font-semibold w-14 sticky top-0 bg-inherit border-[var(--border-color)]">
                  <div className="flex items-center text-gray-400 gap-0.5 cursor-pointer" onClick={() => handleSort('pos')}>
                    Position
                    <span className="text-blue-500">{getSortIcon('pos')}</span>
                  </div>
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-14 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Total Buy
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-14 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Total Sell
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Avg Buy
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Avg Sell
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Realized
                </th>
                <th className="px-2 py-1 border-b  text-gray-400 text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Unrealized
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-12 sticky top-0 bg-inherit border-[var(--border-color)]">
                  LTP
                </th>
                <th className="px-2 py-1 border-b text-gray-400 text-left font-semibold w-12 sticky top-0 bg-inherit border-[var(--border-color)]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="animate-pulse">
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                    <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-8"></div></td>
                  </tr>
                ))
              ) : currentRows.length === 0 ? (
                <tr className="h-full">
                  <td colSpan={11} className="h-full">
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                      <div className="text-3xl text-[var(--text-muted)]">📊</div>
                      <p className="text-xs font-medium text-gray-400 mt-2 text-[var(--text-secondary)]">
                        No positions found
                      </p>
                      <p className="text-[10px] mt-1 text-[var(--text-light)]">
                        {searchText ? 'Try adjusting your search' : 'No positions available'}
                      </p>
                      <button 
                        onClick={handleRefresh}
                        className="mt-3 px-3 py-1 text-[10px] rounded-md bg-blue-500 text-white hover:bg-blue-600"
                      >
                        Refresh
                      </button>
                    </div>
                   </td>
                 </tr>
              ) : (
                currentRows.map((row, idx) => (
                  <tr key={row.trading_symbol || idx} className="transition-colors duration-150 hover:bg-[var(--bg-hover)]">
                    <td className={tableCellClass}>{row.account_id}</td>
                    <td className={`${tableCellClass} font-medium`}>{row.trading_symbol}</td>
                    <td className={`${tableCellClass} font-semibold ${
                      row.pos > 0 ? 'text-green-600 dark:text-green-400' : 
                      row.pos < 0 ? 'text-red-600 dark:text-red-400' : ''
                    }`}>{row.pos}</td>
                    <td className={tableCellClass}>{row.total_bought}</td>
                    <td className={tableCellClass}>{row.total_sold}</td>
                    <td className={tableCellClass}>₹{row.avg_buy?.toFixed(2) || '0.00'}</td>
                    <td className={tableCellClass}>₹{row.avg_sell?.toFixed(2) || '0.00'}</td>
                    <td className={`${tableCellClass} font-semibold ${
                      row.realized > 0 ? 'text-green-600 dark:text-green-400' : 
                      row.realized < 0 ? 'text-red-600 dark:text-red-400' : ''
                    }`}>₹{row.realized?.toFixed(2) || '0.00'}</td>
                    <td className={`${tableCellClass} font-semibold ${
                      row.unrealized > 0 ? 'text-green-600 dark:text-green-400' : 
                      row.unrealized < 0 ? 'text-red-600 dark:text-red-400' : ''
                    }`}>₹{row.unrealized?.toFixed(2) || '0.00'}</td>
                    <td className={tableCellClass}>₹{row.ltp?.toFixed(2) || '0.00'}</td>
                    <td className={tableCellClass}>
                      {row.pos !== 0 && (
                        <button onClick={() => handleExitRowPosition(row.ins_id, row.pos)} className={exitBtnClass}>
                          Exit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex-shrink-0 flex justify-between items-center px-2 py-1 border-t text-[10px] border-[var(--border-color)] bg-[var(--bg-card)]">
            <div className="text-[10px] text-[var(--text-muted)]">
              {startIdx + 1}-{Math.min(startIdx + rowsPerPage, filteredData.length)} of {filteredData.length}
            </div>
            <div className="flex gap-0.5">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-1 py-0.5 text-[10px] rounded transition-all
                  ${currentPage === 1 
                    ? 'text-[var(--text-light)] cursor-not-allowed'
                    : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                  }`}
              >
                ←
              </button>
              
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = i + 1;
                } else if (currentPage <= 2) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNum = totalPages - 2 + i;
                } else {
                  pageNum = currentPage - 1 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-5 h-4 text-[10px] rounded transition-all
                      ${currentPage === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-1 py-0.5 text-[10px] rounded transition-all
                  ${currentPage === totalPages 
                    ? 'text-[var(--text-light)] cursor-not-allowed'
                    : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                  }`}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Exit All Modal */}
      <Modal show={exitPopup} onHide={() => setExitPopup(false)} centered size="sm" className="rounded-modal">
        <Modal.Header closeButton className={`py-1.5 rounded-t-lg ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100'}`}>
          <Modal.Title className={`text-xs ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Close All Positions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`p-3 ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>
          <p className="text-[11px] text-center">Are you sure you want to close all positions?</p>
          <div className="flex justify-center gap-2 mt-3">
            <button onClick={handleCloseAllPosition} className="px-3 py-1 text-[10px] rounded-md bg-green-600 hover:bg-green-700 text-white">
              Yes
            </button>
            <button onClick={() => setExitPopup(false)} className="px-3 py-1 text-[10px] rounded-md bg-red-600 hover:bg-red-700 text-white">
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Set Threshold Modal */}
      <Modal show={showLimit} onHide={() => setShowLimit(false)} centered size="sm" className="rounded-modal">
        <Modal.Header closeButton className={`py-1.5 rounded-t-lg ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100'}`}>
          <Modal.Title className={`text-xs ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Set Profit/Loss Threshold
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`p-3 ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>
          <div className="space-y-2">
            <div>
              <label className={`text-[9px] font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Daily Profit Amount
              </label>
              <input
                type="number"
                value={profitAmount}
                onChange={(e) => setProfitAmount(Number(e.target.value))}
                className={`w-full mt-0.5 px-2 py-1 text-[10px] rounded-md border
                  ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div>
              <label className={`text-[9px] font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Daily Loss Amount
              </label>
              <input
                type="number"
                value={lossAmount}
                onChange={(e) => setLossAmount(Number(e.target.value))}
                className={`w-full mt-0.5 px-2 py-1 text-[10px] rounded-md border
                  ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div className="flex justify-center gap-2 mt-3">
              <button onClick={updateProfitLoss} className="px-3 py-1 text-[10px] rounded-md bg-green-600 hover:bg-green-700 text-white">
                Submit
              </button>
              <button onClick={() => setShowLimit(false)} className="px-3 py-1 text-[10px] rounded-md bg-gray-500 hover:bg-gray-600 text-white">
                Close
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <style jsx global>{`
        .rounded-modal .modal-content {
          border-radius: 12px !important;
          overflow: hidden !important;
        }
        .rounded-modal .modal-header {
          border-top-left-radius: 12px !important;
          border-top-right-radius: 12px !important;
        }
        .rounded-modal .modal-footer {
          border-bottom-left-radius: 12px !important;
          border-bottom-right-radius: 12px !important;
        }
      `}</style>
    </div>
  );
};

export default PositionTable;